import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
gsap.registerPlugin(ScrollToPlugin, ScrollTrigger);

class ProductScroller {
  constructor(element, options) {
    const defaults = {
      initAttr: "data-productscroller",
      initializeAttr: "data-productscroller-initialized",
      timingAttr: "data-productscroller-timing",
      activeAttr: "data-productscroller-active",
      currentAttr: "data-productscroller-current",
      srItemValue: "sr-item",
      containerValue: "container",
      featureValue: "feature",
      buttonValue: "button",
      stickyContentValue: "sticky-content",
      breakpoint: 768,
      baseUrl: "/",
      assetBasePath: "/.resources/brand-lm/webresources/product-scroller",
    };

    this.settings = Object.assign({}, defaults, options);

    this.$root = element;
    this.$canvas = this.$root.querySelector("canvas");
    this.context = this.$canvas.getContext("2d");
    this.$$features = this.$root.querySelectorAll(`[${this.settings.timingAttr}]`);
    this.$$srItems = this.$root.querySelectorAll(
      `[${this.settings.initAttr}="${this.settings.srItemValue}"]`,
    );
    this.$stickyContent = this.$root.querySelector(
      `[${this.settings.initAttr}="${this.settings.stickyContentValue}"]`,
    );
    this.$button = this.$root.querySelector(
      `[${this.settings.initAttr}="${this.settings.buttonValue}"]`,
    );

    this.scrollEffect = null;
    this.scrollAnimation = null;
    this.buttonAnimation = null;
    this.stickyAnimation = null;
    this.timings = {};
    this.frame = 0;
    this.totalFrames = 400;
    this.videoDuration = 16;
    this.images = [];
    this.resolution = this.resolution =
      window.innerHeight <= 800 ? "low" : window.innerHeight <= 1000 ? "normal" : "high";
  }

  initialize() {
    this.prepareData(() => {
      this.setupCanvas();
      this.loadFrames(() => {
        this.renderFrame();
        this.updateSiblings();
        this.initializeAnimation();
        this.$root.setAttribute(this.settings.initializeAttr, "");
        this.improveAccessibility();
      });
    });
  }

  setupCanvas() {
    const parent = this.$canvas.parentElement;
    const rect = parent.getBoundingClientRect();

    this.$canvas.width = rect.width;
    this.$canvas.height = rect.height;
    this.$canvas.style.width = `${rect.width}px`;
    this.$canvas.style.height = `${rect.height}px`;
    this.context.setTransform(1, 0, 0, 1, 0, 0);
  }

  loadFrames(callback) {
    const step = 4;
    let loaded = 0;
    const phaseOneTotal = Math.ceil(this.totalFrames / step);

    for (let i = 1; i <= this.totalFrames; i += step) {
      const img = new Image();
      const number = String(i).padStart(3, "0");
      img.src = `${this.settings.baseUrl}${this.settings.assetBasePath}/frames/low/final_brita_anim_${number}.webp`;
      img.onload = () => {
        this.images[i] = img;
        loaded++;
        if (loaded === phaseOneTotal) {
          callback?.();
          this.loadPhaseTwo();
        }
      };
    }
  }

  loadPhaseTwo() {
    const step = 2;
    let loaded = 0;
    const toLoad = [];

    for (let i = 1; i <= this.totalFrames; i += step) {
      if (this.images[i]) continue;
      toLoad.push(i);
    }

    if (toLoad.length === 0) {
      this.loadPhaseThree();
      return;
    }

    toLoad.forEach((i) => {
      const img = new Image();
      const number = String(i).padStart(3, "0");
      img.src = `${this.settings.baseUrl}${this.settings.assetBasePath}/frames/low/final_brita_anim_${number}.webp`;
      img.onload = () => {
        this.images[i] = img;
        loaded++;
        if (loaded === toLoad.length) {
          this.loadPhaseThree();
        }
      };
    });
  }

  loadPhaseThree() {
    let loaded = 0;
    const toLoad = [];

    for (let i = 1; i <= this.totalFrames; i++) {
      if (this.images[i]) continue;
      toLoad.push(i);
    }

    if (toLoad.length === 0) {
      return;
    }

    toLoad.forEach((i) => {
      const img = new Image();
      const number = String(i).padStart(3, "0");
      img.src = `${this.settings.baseUrl}${this.settings.assetBasePath}/frames/low/final_brita_anim_${number}.webp`;
      img.onload = () => {
        this.images[i] = img;

        loaded++;
        if (loaded === toLoad.length && this.resolution !== "low") {
          this.upgradeImagesToHighQuality();
        }
      };
    });
  }

  upgradeImagesToHighQuality() {
    for (let i = 1; i <= this.totalFrames; i++) {
      const number = String(i).padStart(3, "0");
      const img = new Image();
      img.src = `${this.settings.baseUrl}${this.settings.assetBasePath}/frames/${this.resolution}/final_brita_anim_${number}.webp`;
      img.onload = () => {
        this.images[i] = img;
      };
    }
  }

  renderFrame() {
    const img = this.images[this.frame];
    if (!img || !img.complete) return;

    const ctx = this.context;
    const canvas = this.$canvas;

    const scale = canvas.height / img.height;
    const drawWidth = img.width * scale;
    const drawHeight = canvas.height;

    const x = (canvas.width - drawWidth) / 2;
    const y = 0;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, x, y, drawWidth, drawHeight);
  }

  initializeAnimation() {
    this.scrollEffect = gsap.timeline({ paused: true });

    this.scrollEffect.to(this, {
      frame: this.totalFrames - 1,
      ease: "none",
      onUpdate: () => {
        this.frame = Math.floor(this.frame);
        this.renderFrame();
      },
    });

    this.scrollAnimation = ScrollTrigger.create({
      trigger: this.$root,
      start: "top center",
      end: "bottom -200%",
      onUpdate: (self) => {
        const progress = Math.min(1, Math.max(0, self.progress));
        this.scrollEffect.progress(progress);
        this.toggleFeatureVisibility();
      },
    });

    this.buttonAnimation = ScrollTrigger.create({
      trigger: this.$root,
      start: "top 10%",
      end: "bottom -150%",
      onUpdate: (self) => {
        const progress = Math.min(1, Math.max(0, self.progress));
        this.toggleButtonVisibility(progress > 0 && progress < 1);
      },
    });

    if (this.$stickyContent) {
      this.stickyAnimation = ScrollTrigger.create({
        trigger: this.$root,
        start: "top top",
        end: "bottom -200%",
        pin: this.$stickyContent,
      });
    }

    ScrollTrigger.refresh();
  }

  updateSiblings() {
    this.$root.previousElementSibling?.style.setProperty("margin-bottom", 0);
    this.$root.nextElementSibling?.style.setProperty("margin-top", 0);
  }

  improveAccessibility() {
    this.$$srItems.forEach(($srItem) => {
      $srItem.setAttribute("tabindex", -1);

      $srItem.addEventListener("focus", () => {
        const $parentFeature = $srItem.closest(`[${this.settings.timingAttr}]`);
        const seconds = Number($parentFeature?.getAttribute(this.settings.timingAttr)) || 0;
        const frame = Math.round(seconds * (this.totalFrames / this.videoDuration));
        const progress = frame / this.totalFrames;
        const st = this.scrollEffect.scrollTrigger;

        const scrollStart = st.start;
        const scrollEnd = st.end;
        const targetScroll = (scrollEnd - scrollStart - this.$root.clientHeight) * progress;

        gsap.to(window, {
          scrollTo: targetScroll,
          duration: 1,
          ease: "power2.out",
        });
      });
    });
  }

  toggleButtonVisibility(visible) {
    this.$button?.setAttribute(this.settings.activeAttr, visible);
    this.$button?.setAttribute("aria-hidden", !visible);

    if (visible) {
      this.$button?.removeAttribute("tabindex");
    } else {
      this.$button?.setAttribute("tabindex", "-1");
    }
  }

  toggleFeatureVisibility() {
    const setCurrentEntry = () => {
      let latestKey = null;
      let latestStartTime = -Infinity;

      for (const key in this.timings) {
        const entry = this.timings[key];
        if (entry.startTime <= this.frame && entry.startTime > latestStartTime) {
          latestStartTime = entry.startTime;
          latestKey = key;
        }
      }

      for (const key in this.timings) {
        this.timings[key].current = key === latestKey;
      }
    };

    this.$$features.forEach(($feature, index) => {
      this.timings[index].active = this.frame >= this.timings[index].startTime;
    });

    setCurrentEntry();

    this.$$features.forEach(($feature, index) => {
      $feature.setAttribute(this.settings.currentAttr, this.timings[index].current);
      $feature.setAttribute(this.settings.activeAttr, this.timings[index].active);
    });
  }

  prepareData(callback) {
    this.$$features.forEach(($feature, index) => {
      const seconds = parseFloat($feature.getAttribute(this.settings.timingAttr)) || 0;
      const frame = Math.round(seconds * (this.totalFrames / this.videoDuration));

      this.timings[index] = {
        startTime: frame,
        active: false,
        current: false,
      };
    });

    this.duration = this.totalFrames;
    if (typeof callback === "function") callback();
  }
}

export { ProductScroller };
